<template>
    <!-- Token directory section -->
    <div class="rounded-xl border border-taupe-200 bg-white p-6 sm:p-7 mb-6">
        <div class="sm:flex sm:items-center sm:justify-between">
            <div>
                <h3 class="text-lg font-medium leading-6 text-taupe-800">
                    API Token Directory
                </h3>
                <p class="mt-1 max-w-2xl text-sm text-taupe-500">
                    All Management API tokens across the account. Create,
                    rotate, and manage tokens from one place.
                </p>
            </div>
            <div
                class="mt-4 sm:mt-0 sm:ml-4 sm:shrink-0 flex items-center gap-3"
            >
                <label
                    class="flex items-center gap-2 text-sm text-taupe-600 cursor-pointer"
                >
                    <input
                        type="checkbox"
                        v-model="showRevoked"
                        class="h-4 w-4 rounded border-taupe-300 text-sky-600 focus:ring-sky-500"
                    />
                    Show revoked
                </label>
                <zen-button
                    variant="secondary"
                    @click="showBulkRevokeModal = true"
                >
                    Bulk revoke
                </zen-button>
                <zen-button
                    v-if="hasPlanFeature('management_api_access')"
                    variant="primary"
                    @click="showCreateModal = true"
                >
                    Create token
                </zen-button>
            </div>
        </div>
        <p
            v-if="!hasPlanFeature('management_api_access')"
            class="mt-2 text-sm text-taupe-500 italic"
        >
            Creating new tokens needs a higher plan — no sales call, just
            upgrade in Billing.
        </p>

        <!-- Token table -->
        <div class="mt-6">
            <div v-if="loading" class="text-sm text-taupe-500 py-4">
                Loading tokens…
            </div>
            <div
                v-else-if="tokens.length === 0"
                class="rounded-lg border border-dashed border-taupe-300 bg-taupe-50"
            >
                <zen-empty-table-message compact icon="ZenIconToken">
                    No API tokens yet. Create one to get started.
                </zen-empty-table-message>
            </div>
            <div v-else class="overflow-x-auto">
                <table
                    class="min-w-full divide-y divide-taupe-200"
                    aria-label="API tokens"
                >
                    <thead>
                        <tr>
                            <th
                                class="py-3 text-left text-xs font-medium text-taupe-500 uppercase tracking-wider"
                            >
                                Name
                            </th>
                            <th
                                class="py-3 text-left text-xs font-medium text-taupe-500 uppercase tracking-wider"
                            >
                                Scopes
                            </th>
                            <th
                                class="py-3 text-left text-xs font-medium text-taupe-500 uppercase tracking-wider"
                            >
                                Last used
                            </th>
                            <th
                                class="py-3 text-left text-xs font-medium text-taupe-500 uppercase tracking-wider"
                            >
                                Expires
                            </th>
                            <th
                                class="py-3 text-left text-xs font-medium text-taupe-500 uppercase tracking-wider"
                            >
                                Status
                            </th>
                            <th class="py-3" aria-label="Actions"></th>
                        </tr>
                    </thead>
                    <tbody class="divide-y divide-taupe-100">
                        <tr
                            v-for="token in tokens"
                            :key="token.ulid"
                            class="hover:bg-taupe-50"
                        >
                            <td class="py-3 text-sm font-medium text-taupe-800">
                                {{ token.name }}
                            </td>
                            <td class="py-3">
                                <div class="flex flex-wrap items-center gap-1">
                                    <span
                                        v-for="pill in visiblePills(token)"
                                        :key="`${pill.type}:${pill.label}`"
                                        :class="pillClass(pill.type)"
                                        class="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset"
                                    >
                                        {{ pill.label }}
                                    </span>
                                    <button
                                        v-if="
                                            pillCount(token) > 2 &&
                                            !isPillsExpanded(token.ulid)
                                        "
                                        type="button"
                                        @click="togglePillsExpanded(token.ulid)"
                                        :aria-label="`Show all: ${token.name}`"
                                        class="cursor-pointer text-xs font-medium text-taupe-500 hover:text-taupe-700"
                                    >
                                        … ({{ pillCount(token) }})
                                    </button>
                                    <button
                                        v-if="
                                            pillCount(token) > 2 &&
                                            isPillsExpanded(token.ulid)
                                        "
                                        type="button"
                                        @click="togglePillsExpanded(token.ulid)"
                                        :aria-label="`Collapse: ${token.name}`"
                                        class="cursor-pointer text-xs font-medium text-taupe-500 hover:text-taupe-700"
                                    >
                                        Show less
                                    </button>
                                </div>
                            </td>
                            <td class="py-3 text-sm text-taupe-500">
                                {{
                                    token.last_used_at
                                        ? formatDate(token.last_used_at)
                                        : 'Never'
                                }}
                            </td>
                            <td class="py-3 text-sm text-taupe-500">
                                {{
                                    token.expires_at
                                        ? formatDate(token.expires_at)
                                        : 'Never'
                                }}
                            </td>
                            <td class="py-3">
                                <span
                                    v-if="token.revoked_at"
                                    class="inline-flex items-center rounded-full bg-taupe-100 px-2.5 py-0.5 text-xs font-medium text-taupe-500"
                                >
                                    Revoked
                                </span>
                                <span
                                    v-else-if="
                                        token.replaced_by_ulid &&
                                        token.grace_period_ends_at
                                    "
                                    class="inline-flex items-center rounded-full bg-amber-50 px-2.5 py-0.5 text-xs font-medium text-amber-700 ring-1 ring-inset ring-amber-600/10"
                                >
                                    Grace period
                                </span>
                                <span
                                    v-else
                                    class="inline-flex items-center rounded-full bg-green-50 px-2.5 py-0.5 text-xs font-medium text-green-700 ring-1 ring-inset ring-green-600/10"
                                >
                                    Active
                                </span>
                            </td>
                            <td class="py-3 text-right">
                                <div
                                    v-if="!token.revoked_at"
                                    class="flex items-center justify-end gap-3"
                                >
                                    <zen-button
                                        variant="link-muted"
                                        @click="openUsageLogModal(token)"
                                        :aria-label="`Usage log: ${token.name}`"
                                    >
                                        Usage
                                    </zen-button>
                                    <zen-button
                                        variant="link-muted"
                                        @click="openPermissionsModal(token)"
                                        :aria-label="`Effective permissions: ${token.name}`"
                                    >
                                        Permissions
                                    </zen-button>
                                    <zen-button
                                        variant="link"
                                        @click="openEditModal(token)"
                                        :aria-label="`Edit token: ${token.name}`"
                                    >
                                        Edit
                                    </zen-button>
                                    <zen-button
                                        variant="link"
                                        @click="confirmRotate(token)"
                                        :aria-label="`Rotate token: ${token.name}`"
                                    >
                                        Rotate
                                    </zen-button>
                                    <zen-button
                                        variant="link-danger"
                                        @click="confirmRevoke(token)"
                                        :aria-label="`Revoke token: ${token.name}`"
                                    >
                                        Revoke
                                    </zen-button>
                                </div>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <zen-pagination
                v-if="!loading"
                :page="pagination.page"
                :total-pages="pagination.total_pages"
                :total="pagination.total"
                @change="changePage"
            />
        </div>
    </div>

    <create-api-token-modal
        v-if="showCreateModal"
        :is-open="showCreateModal"
        v-model:form="createForm"
        :available-scopes="availableScopes"
        :projects="projects"
        :processing="creating"
        :error="createError"
        @close="showCreateModal = false"
        @submit="createToken"
    />

    <api-token-created-modal
        v-if="newToken"
        :is-open="!!newToken"
        :token="newToken"
        :copied="copied"
        @close="dismissNewToken"
        @copy="copyToken"
    />

    <edit-api-token-scopes-modal
        v-if="editTarget"
        :is-open="!!editTarget"
        :target="editTarget"
        v-model:form="editForm"
        :available-scopes="availableScopes"
        :projects="projects"
        :processing="updatingScopes"
        :error="editError"
        @close="editTarget = null"
        @submit="updateTokenScopes"
    />

    <effective-api-token-permissions-modal
        v-if="permissionsTarget"
        :is-open="!!permissionsTarget"
        :target="permissionsTarget"
        :loading="loadingPermissions"
        :permissions="effectivePermissions"
        @close="permissionsTarget = null"
    />

    <revoke-api-token-modal
        v-if="revokeTarget"
        :is-open="!!revokeTarget"
        :target="revokeTarget"
        :processing="revoking"
        @close="revokeTarget = null"
        @confirm="revokeToken"
    />

    <rotate-api-token-modal
        v-if="rotateTarget"
        :is-open="!!rotateTarget"
        :target="rotateTarget"
        v-model="rotateGracePeriodHours"
        :processing="rotating"
        @close="rotateTarget = null"
        @confirm="rotateToken"
    />

    <bulk-revoke-api-tokens-modal
        v-if="showBulkRevokeModal"
        :is-open="showBulkRevokeModal"
        v-model:form="bulkRevokeForm"
        :search-users="searchUsers"
        :processing="bulkRevoking"
        :error="bulkRevokeError"
        @close="showBulkRevokeModal = false"
        @submit="executeBulkRevoke"
    />

    <api-token-usage-log-modal
        v-if="usageLogTarget"
        :is-open="!!usageLogTarget"
        :target="usageLogTarget"
        :loading="loadingUsageLog"
        :logs="usageLogs"
        :format-date-time="formatDateTime"
        @close="usageLogTarget = null"
    />
</template>

<script>
import managementApiTokenService from '@/services/management-api-token.service'
import userService from '@/services/user.service'
import { errorMessage, successMessage } from '@/utils/notifications'
import {
    findProjectByUlid,
    findProjectByEnvironmentUlid,
} from '@/utils/project-environments'
import { removeBy, upsertBy } from '@/utils/list-mutations'
import CreateApiTokenModal from '@/screens/_components/admin/modals/CreateApiTokenModal.vue'
import ApiTokenCreatedModal from '@/screens/_components/admin/modals/ApiTokenCreatedModal.vue'
import EditApiTokenScopesModal from '@/screens/_components/admin/modals/EditApiTokenScopesModal.vue'
import EffectiveApiTokenPermissionsModal from '@/screens/_components/admin/modals/EffectiveApiTokenPermissionsModal.vue'
import RevokeApiTokenModal from '@/screens/_components/admin/modals/RevokeApiTokenModal.vue'
import RotateApiTokenModal from '@/screens/_components/admin/modals/RotateApiTokenModal.vue'
import BulkRevokeApiTokensModal from '@/screens/_components/admin/modals/BulkRevokeApiTokensModal.vue'
import ApiTokenUsageLogModal from '@/screens/_components/admin/modals/ApiTokenUsageLogModal.vue'
import { useSessionStore } from '@/stores/session'
import { usePlan } from '@/composables/usePlan'
import { useServerPagination } from '@/composables/useServerPagination'

const AVAILABLE_SCOPES = [
    {
        value: 'projects:read',
        label: 'projects:read',
        description: '— list projects and environments',
    },
    {
        value: 'flags:read',
        label: 'flags:read',
        description: '— read flag configurations',
    },
    {
        value: 'flags:write',
        label: 'flags:write',
        description: '— create and update flags',
    },
    {
        value: 'targets:read',
        label: 'targets:read',
        description: '— read targeting rules',
    },
    {
        value: 'targets:write',
        label: 'targets:write',
        description: '— manage targeting rules',
    },
    {
        value: 'config:write',
        label: 'config:write',
        description: '— update account configuration',
    },
    {
        value: 'audit:read',
        label: 'audit:read',
        description: '— read the account and public API audit trails',
    },
]

// The raw secret is only shown once, from the create or rotate response. It
// never belongs on a row in the directory.
function toListItem(token) {
    const item = { ...token }
    delete item.raw_token
    delete item.shown_once
    return item
}

export default {
    name: 'ManagementApiTokensPanel',
    components: {
        CreateApiTokenModal,
        ApiTokenCreatedModal,
        EditApiTokenScopesModal,
        EffectiveApiTokenPermissionsModal,
        RevokeApiTokenModal,
        RotateApiTokenModal,
        BulkRevokeApiTokensModal,
        ApiTokenUsageLogModal,
    },
    setup() {
        const { hasPlanFeature } = usePlan()
        const {
            pagination,
            setPage,
            resetPage,
            applyResponse,
            shiftTotal,
            resetOnError,
        } = useServerPagination({ perPage: 25 })
        return {
            hasPlanFeature,
            pagination,
            setPage,
            resetPage,
            applyResponse,
            shiftTotal,
            resetOnError,
        }
    },
    data() {
        return {
            loading: true,
            tokens: [],
            showCreateModal: false,
            creating: false,
            createError: null,
            createForm: {
                name: '',
                scopes: [],
                expires_at: '',
                resource_grants: [],
            },
            newToken: null,
            copied: false,
            revokeTarget: null,
            revoking: false,
            availableScopes: AVAILABLE_SCOPES,

            // Edit scopes modal
            editTarget: null,
            editForm: {
                scopes: [],
                resource_grants: [],
            },
            updatingScopes: false,
            editError: null,

            // Effective permissions modal
            permissionsTarget: null,
            loadingPermissions: false,
            effectivePermissions: null,

            // Directory view
            showRevoked: false,
            expandedPillTokenUlids: new Map(),

            // Rotation
            rotateTarget: null,
            rotating: false,
            rotateGracePeriodHours: 24,

            // Bulk revoke
            showBulkRevokeModal: false,
            bulkRevoking: false,
            bulkRevokeForm: {
                user_ulid: '',
                unused_days: null,
            },
            bulkRevokeError: null,

            // Usage log
            usageLogTarget: null,
            loadingUsageLog: false,
            usageLogs: [],
        }
    },
    computed: {
        sessionStore() {
            return useSessionStore()
        },
        projects() {
            return this.sessionStore.data?.projects?.data ?? []
        },
    },
    watch: {
        showRevoked() {
            // The filter is applied server-side, so the current page number no
            // longer means anything against the new result set.
            this.resetPage()
            this.loadTokens()
        },
    },
    methods: {
        changePage(page) {
            this.setPage(page)
            this.loadTokens()
        },
        formatDate(iso) {
            if (!iso) return '—'
            return new Date(iso).toLocaleDateString(undefined, {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
            })
        },
        formatDateTime(iso) {
            if (!iso) return '—'
            return new Date(iso).toLocaleString(undefined, {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
            })
        },
        allPills(token) {
            const scopePills = (token.scopes ?? []).map((scope) => ({
                type: 'scope',
                label: scope,
            }))
            const grantPills = this.resourceGrantLabels(token).map((label) => ({
                type: 'grant',
                label,
            }))
            return [...scopePills, ...grantPills]
        },
        pillCount(token) {
            return (
                (token.scopes ?? []).length +
                (token.resource_grants ?? []).length
            )
        },
        visiblePills(token) {
            const pills = this.allPills(token)
            if (this.isPillsExpanded(token.ulid) || pills.length <= 2) {
                return pills
            }
            return pills.slice(0, 2)
        },
        pillClass(type) {
            return type === 'grant'
                ? 'bg-taupe-100 text-taupe-600 ring-taupe-500/10'
                : 'bg-sky-50 text-sky-700 ring-sky-700/10'
        },
        isPillsExpanded(tokenUlid) {
            return this.expandedPillTokenUlids.get(tokenUlid) === true
        },
        togglePillsExpanded(tokenUlid) {
            this.expandedPillTokenUlids.set(
                tokenUlid,
                !this.isPillsExpanded(tokenUlid)
            )
        },
        resourceGrantLabels(token) {
            return (token.resource_grants ?? []).map((grant) =>
                this.resourceGrantLabel(grant)
            )
        },
        resourceGrantLabel(grant) {
            if (grant.resource_type === 'project') {
                const project = findProjectByUlid(
                    this.projects,
                    grant.resource_ulid
                )
                return project
                    ? `${project.name} (all environments)`
                    : grant.resource_ulid
            }

            if (grant.resource_type === 'environment') {
                const project = findProjectByEnvironmentUlid(
                    this.projects,
                    grant.resource_ulid
                )
                const environment = project?.environments?.data?.find(
                    (env) => env.ulid === grant.resource_ulid
                )
                return project && environment
                    ? `${project.name}: ${environment.name}`
                    : grant.resource_ulid
            }

            return grant.resource_ulid
        },
        // A new token goes at the top of page one. On later pages only the
        // total moves, and the token shows up when the admin returns to page one.
        addTokenToTop(token) {
            this.shiftTotal(1)
            if (this.pagination.page !== 1) return

            this.tokens = upsertBy(this.tokens, token, 'ulid', {
                position: 'prepend',
            }).slice(0, this.pagination.per_page)
        },
        // Active-only view: the revoked token leaves the list. "Show revoked"
        // view: the row stays, with its revoked state from the response.
        async applyRevoke(ulid, revokedToken) {
            // The row may have moved off this page since it was clicked. Then
            // the local list can't be trusted, so reload rather than guess.
            if (!this.tokens.some((token) => token.ulid === ulid)) {
                await this.loadTokens()
                return
            }

            if (this.showRevoked) {
                this.tokens = upsertBy(this.tokens, revokedToken)
                return
            }

            this.tokens = removeBy(this.tokens, ulid)
            this.shiftTotal(-1)
            // Emptying a later page leaves nothing to show, so step back a
            // page. This is the one place a revoke still reloads.
            if (this.tokens.length === 0 && this.pagination.page > 1) {
                this.setPage(this.pagination.page - 1)
                await this.loadTokens()
            }
        },
        async loadTokens() {
            this.loading = true
            try {
                const response = await managementApiTokenService.directory({
                    page: this.pagination.page,
                    perPage: this.pagination.per_page,
                    // Revoked tokens are excluded server-side rather than filtered
                    // out of the page here — filtering a page after the fact would
                    // leave short pages and a misleading total.
                    status: this.showRevoked ? undefined : 'active',
                })
                this.tokens = response.data ?? []

                if (this.applyResponse(response.meta)) {
                    return await this.loadTokens()
                }
            } catch {
                errorMessage('Failed to load API tokens.')
                this.tokens = []
                this.resetOnError()
            } finally {
                this.loading = false
            }
        },
        async searchUsers(query) {
            try {
                const response = await userService.search({
                    search: query,
                    per_page: 20,
                })
                return (response.data ?? []).map((user) => ({
                    ulid: user.ulid,
                    label: `${user.display_name} (${user.email})`,
                }))
            } catch (error) {
                errorMessage(error)
                return []
            }
        },
        async createToken() {
            this.creating = true
            this.createError = null
            try {
                const payload = {
                    name: this.createForm.name,
                    scopes: this.createForm.scopes,
                }
                if (this.createForm.expires_at) {
                    payload.expires_at = this.createForm.expires_at
                }
                // Grants go in the create request itself: the API stores the
                // token and its grants in one transaction, so there is no
                // unrestricted window to clean up after.
                if (this.createForm.resource_grants.length > 0) {
                    payload.resource_grants = this.createForm.resource_grants
                }
                const response = await managementApiTokenService.create(payload)

                this.newToken = response.data?.raw_token ?? null
                this.addTokenToTop(toListItem(response.data))
                this.showCreateModal = false
                this.createForm = {
                    name: '',
                    scopes: [],
                    expires_at: '',
                    resource_grants: [],
                }
            } catch (error) {
                this.createError =
                    error?.message ??
                    'Failed to create token. Please try again.'
            } finally {
                this.creating = false
            }
        },
        async copyToken() {
            try {
                await navigator.clipboard.writeText(this.newToken)
                this.copied = true
                setTimeout(() => {
                    this.copied = false
                }, 2000)
            } catch {
                errorMessage(
                    'Could not copy to clipboard. Please select and copy the token manually.'
                )
            }
        },
        dismissNewToken() {
            this.newToken = null
            this.copied = false
        },
        confirmRevoke(token) {
            this.revokeTarget = token
        },
        async revokeToken() {
            if (!this.revokeTarget) return
            this.revoking = true
            const target = this.revokeTarget
            try {
                const response = await managementApiTokenService.revoke(
                    target.ulid
                )
                successMessage('Token revoked.')
                this.revokeTarget = null
                await this.applyRevoke(target.ulid, response.data)
            } catch {
                errorMessage('Failed to revoke token.')
            } finally {
                this.revoking = false
            }
        },

        // -----------------------------------------------------------------------
        // Rotation
        // -----------------------------------------------------------------------
        confirmRotate(token) {
            this.rotateTarget = token
            this.rotateGracePeriodHours = 24
        },
        async rotateToken() {
            if (!this.rotateTarget) return
            if (
                this.rotateGracePeriodHours < 1 ||
                this.rotateGracePeriodHours > 168
            ) {
                errorMessage('Grace period must be between 1 and 168 hours.')
                return
            }
            this.rotating = true
            const target = this.rotateTarget
            try {
                const response = await managementApiTokenService.rotate(
                    target.ulid,
                    { grace_period_hours: this.rotateGracePeriodHours }
                )
                this.newToken = response.data?.raw_token ?? null
                this.rotateTarget = null
                // The old token stays listed through its grace period, now
                // marked as replaced, so swap in the copy the API returns.
                const replaced = response.meta?.replaced_token
                if (
                    replaced &&
                    this.tokens.some((t) => t.ulid === replaced.ulid)
                ) {
                    this.tokens = upsertBy(this.tokens, replaced)
                }
                this.addTokenToTop(toListItem(response.data))
            } catch {
                errorMessage('Failed to rotate token.')
            } finally {
                this.rotating = false
            }
        },

        // -----------------------------------------------------------------------
        // Bulk revoke
        // -----------------------------------------------------------------------
        async executeBulkRevoke() {
            this.bulkRevoking = true
            this.bulkRevokeError = null
            try {
                const payload = {}
                if (this.bulkRevokeForm.user_ulid) {
                    payload.user_ulid = this.bulkRevokeForm.user_ulid
                }
                if (this.bulkRevokeForm.unused_days) {
                    payload.unused_days = this.bulkRevokeForm.unused_days
                }
                const response =
                    await managementApiTokenService.bulkRevoke(payload)
                const count = response.data?.tokens_revoked ?? 0
                successMessage(`${count} token(s) revoked.`)
                this.showBulkRevokeModal = false
                this.bulkRevokeForm = { user_ulid: '', unused_days: null }
                await this.loadTokens()
            } catch (error) {
                this.bulkRevokeError =
                    error?.message ?? 'Failed to bulk revoke tokens.'
            } finally {
                this.bulkRevoking = false
            }
        },

        // -----------------------------------------------------------------------
        // Usage log
        // -----------------------------------------------------------------------
        async openUsageLogModal(token) {
            this.usageLogTarget = token
            this.usageLogs = []
            this.loadingUsageLog = true
            try {
                const response = await managementApiTokenService.usageLog(
                    token.ulid
                )
                this.usageLogs = response.data ?? []
            } catch {
                errorMessage('Failed to load usage log.')
                this.usageLogTarget = null
            } finally {
                this.loadingUsageLog = false
            }
        },

        // -----------------------------------------------------------------------
        // Edit token scopes
        // -----------------------------------------------------------------------
        openEditModal(token) {
            this.editTarget = token
            this.editForm = {
                scopes: [...(token.scopes ?? [])],
                resource_grants: (token.resource_grants ?? []).map((g) => ({
                    resource_type: g.resource_type,
                    resource_ulid: g.resource_ulid,
                })),
            }
            this.editError = null
        },
        async updateTokenScopes() {
            if (!this.editTarget) return
            this.updatingScopes = true
            this.editError = null
            try {
                const response = await managementApiTokenService.updateScopes(
                    this.editTarget.ulid,
                    {
                        scopes: this.editForm.scopes,
                        resource_grants: this.editForm.resource_grants,
                    }
                )
                successMessage('Token permissions updated.')
                if (this.tokens.some((t) => t.ulid === response.data.ulid)) {
                    this.tokens = upsertBy(this.tokens, response.data)
                } else {
                    // Not on this page any more, so there is no row to update.
                    await this.loadTokens()
                }
                this.editTarget = null
            } catch (error) {
                this.editError =
                    error?.message ??
                    'Failed to update token. Please try again.'
            } finally {
                this.updatingScopes = false
            }
        },

        // -----------------------------------------------------------------------
        // Effective permissions
        // -----------------------------------------------------------------------
        async openPermissionsModal(token) {
            this.permissionsTarget = token
            this.effectivePermissions = null
            this.loadingPermissions = true
            try {
                const response =
                    await managementApiTokenService.getEffectivePermissions(
                        token.ulid
                    )
                this.effectivePermissions = response.data ?? null
            } catch {
                errorMessage('Failed to load effective permissions.')
                this.permissionsTarget = null
            } finally {
                this.loadingPermissions = false
            }
        },
    },
    async mounted() {
        await this.loadTokens()
    },
}
</script>
